/**
 * Process a raw price input value, accepting both ',' and '.' as decimal separators.
 * Displays in European format: dot as thousands separator, comma as decimal.
 *
 * Returns the formatted display string, or null if the input is invalid (caller should ignore).
 * Empty string is valid and returns "".
 *
 * Examples:
 *   "1.5"      → "1,5"       (dot treated as decimal)
 *   "1,5"      → "1,5"       (comma decimal, unchanged)
 *   "1234"     → "1.234"     (auto thousands)
 *   "1234.56"  → "1.234,56"  (dot decimal + auto thousands)
 *   "1234,56"  → "1.234,56"  (comma decimal + auto thousands)
 *   "1.234,56" → "1.234,56"  (already European, unchanged)
 */
export const processPriceInput = (rawValue, maxDecimals = 3) => {
  if (rawValue === "" || rawValue === null || rawValue === undefined) return "";

  let processed = String(rawValue);

  if (!processed.includes(",")) {
    // No comma present — the last dot (if any) is the decimal separator when followed by
    // ≤ maxDecimals digits. Dots followed by more digits are thousands separators and
    // will be stripped below.
    processed = processed.replace(
      new RegExp(`\\.(\\d{0,${maxDecimals}})$`),
      (_, digits) => `,${digits}`
    );
  }

  // Strip all remaining dots — they are thousands separators added by auto-formatting.
  processed = processed.replace(/\./g, "");

  // Validate: only digits, one optional comma, up to maxDecimals decimal digits.
  if (!new RegExp(`^\\d*,?\\d{0,${maxDecimals}}$`).test(processed)) return null;

  const [intPart = "", decPart] = processed.split(",");
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return decPart !== undefined ? `${formattedInt},${decPart}` : formattedInt;
};

/**
 * Parse a European-formatted price string (or plain number) to a float.
 * Accepts: "1.234,56", "1234,56", "1234.56", "1.234.567", 1234.
 * Returns null for empty / null / undefined / unparseable input.
 */
export const parsePrice = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const str = String(value).trim();
  if (str === "") return null;
  // Remove thousands-separator dots, then normalise decimal comma to dot for parseFloat.
  const normalized = str.replace(/\./g, "").replace(",", ".");
  const result = parseFloat(normalized);
  return isNaN(result) ? null : result;
};
